import { ensureValidSession, isSupabaseConfigured, requireSupabase } from '@/lib/supabase';

interface RpcResult {
    [key: string]: unknown;
}

const SHA256_PATTERN = /^[0-9a-f]{64}$/i;
const CONSENT_DOWNLOAD_PROXY_URL = `${import.meta.env.VITE_SUPABASE_URL || 'https://example.supabase.co'}/functions/v1/download-consent-document`;

function asRecord(raw: unknown): RpcResult {
    if (!raw || typeof raw !== 'object') return {};
    return raw as RpcResult;
}

function asStringOrNull(raw: unknown): string | null {
    return typeof raw === 'string' && raw.trim() ? raw : null;
}

function sanitizeRpcPayload(payload?: Record<string, unknown>): Record<string, unknown> {
    if (!payload) return {};

    return Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== null && value !== undefined)
    );
}

function isRetryableTransientError(raw: string): boolean {
    const normalized = raw.toLowerCase();
    return (
        normalized.includes('network') ||
        normalized.includes('timeout') ||
        normalized.includes('temporarily') ||
        normalized.includes('econn') ||
        normalized.includes('503') ||
        normalized.includes('502') ||
        normalized.includes('504') ||
        normalized.includes('fetch')
    );
}

function extractErrorMessage(raw: unknown): string {
    if (!raw) return '';
    if (typeof raw === 'string') return raw.trim();
    if (typeof raw !== 'object') return '';

    const body = raw as Record<string, unknown>;
    const candidates = [
        body.message,
        body.error_description,
        body.details,
        body.error,
        body.code,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
        }

        if (candidate && typeof candidate === 'object') {
            const nestedMessage = extractErrorMessage(candidate);
            if (nestedMessage) return nestedMessage;
        }
    }

    return '';
}

async function readFunctionContextMessage(rawContext: unknown): Promise<string> {
    if (!rawContext || typeof rawContext !== 'object') return '';

    const context =
        typeof (rawContext as { clone?: () => unknown }).clone === 'function'
            ? (rawContext as { clone: () => unknown }).clone()
            : rawContext;

    if (!context || typeof context !== 'object') return '';

    const target = context as { json?: () => Promise<unknown>; text?: () => Promise<string> };

    if (typeof target.json === 'function') {
        try {
            const payload = await target.json();
            const message = extractErrorMessage(payload);
            if (message) return message;
        } catch {
            // Fall back to plain-text parsing below.
        }
    }

    if (typeof target.text === 'function') {
        try {
            return (await target.text()).trim();
        } catch {
            return '';
        }
    }

    return '';
}

async function toEdgeFunctionError(
    functionName: string,
    rawError: { name?: string; message?: string; context?: unknown } | null | undefined,
    fallbackMessage: string
): Promise<Error> {
    const name = typeof rawError?.name === 'string' ? rawError.name : '';
    const message = typeof rawError?.message === 'string' ? rawError.message : '';
    const isFetchError =
        name === 'FunctionsFetchError' ||
        message.includes('Failed to send a request to the Edge Function');

    if (isFetchError) {
        return new Error(
            `Edge Function ${functionName} ist nicht erreichbar. Bitte Deployment, Projekt-URL und CORS (OPTIONS/POST) prüfen.`
        );
    }

    const status =
        rawError?.context && typeof rawError.context === 'object' && typeof (rawError.context as { status?: unknown }).status === 'number'
            ? (rawError.context as { status: number }).status
            : null;
    const extractedMessage = await readFunctionContextMessage(rawError?.context);

    if (status === 401) {
        return new Error('Ihre Anmeldung ist abgelaufen. Bitte Seite neu laden und erneut anmelden.');
    }

    if (status === 403 && !extractedMessage) {
        return new Error('Zugriff auf diese Einwilligung wurde verweigert. Bitte Rollen- und Praxiszuordnung prüfen.');
    }

    return new Error(extractedMessage || message || fallbackMessage);
}

async function readConsentDocumentError(response: Response): Promise<Error | null> {
    let message = '';

    try {
        const payload = asRecord(await response.clone().json());
        message =
            asStringOrNull(payload.error) ||
            asStringOrNull(payload.message) ||
            '';
    } catch {
        try {
            message = (await response.text()).trim();
        } catch {
            message = '';
        }
    }

    if (response.status === 404) {
        return null;
    }

    if (response.status === 401) {
        return new Error('Keine gültige Sitzung. Bitte erneut anmelden.');
    }

    if (response.status === 403) {
        return new Error('Zugriff auf dieses Consent-PDF wurde verweigert.');
    }

    return new Error(message || 'Consent-PDF konnte nicht geladen werden.');
}

async function downloadConsentDocumentUrl(
    consentId: string,
    accessToken: string
): Promise<string | null> {
    const response = await fetch(CONSENT_DOWNLOAD_PROXY_URL, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ consentId }),
    });

    if (!response.ok) {
        const error = await readConsentDocumentError(response);
        if (!error) return null;
        throw error;
    }

    const pdfBlob = await response.blob();
    if (pdfBlob.size <= 0) {
        throw new Error('Consent-PDF konnte nicht geladen werden.');
    }

    if (typeof URL.createObjectURL !== 'function') {
        throw new Error('Browser unterstützt keinen sicheren PDF-Download.');
    }

    return URL.createObjectURL(pdfBlob);
}

function asFinalizedPdfArtifact(consentId: string, generatedDocument: unknown): RpcResult | null {
    const generated = asRecord(generatedDocument);
    const hash = asStringOrNull(generated.artifact_hash_sha256);
    const bucket = asStringOrNull(generated.artifact_bucket);
    const path = asStringOrNull(generated.artifact_path);

    if (!hash || !bucket || !path || !SHA256_PATTERN.test(hash)) {
        return null;
    }

    return {
        consent_id: consentId,
        revision: Number(generated.revision || 1),
        artifact_id: asStringOrNull(generated.artifact_id),
        artifact_hash_sha256: hash,
        artifact_url: asStringOrNull(generated.artifact_url),
        artifact_bucket: bucket,
        artifact_path: path,
        pdf_label:
            asStringOrNull(generated.pdf_label) ||
            `consent-${consentId}-rev-${Number(generated.revision || 1)}.pdf`,
        generated_at:
            asStringOrNull(generated.generated_at) ||
            asStringOrNull(generated.pdf_generated_at) ||
            new Date().toISOString(),
    };
}

async function invokeConsentArtifactGeneration(input: {
    consentId: string;
    actor?: string;
    format?: 'csv';
    fallbackMessage: string;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const maxAttempts = 2;
    let data: unknown = null;
    let error: { message?: string } | null = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const body: Record<string, unknown> = {
            consentId: input.consentId,
            actor: input.actor || null,
        };

        if (input.format) {
            body.format = input.format;
        }

        const result = await sb.functions.invoke('generate-surgical-consent-pdf', { body });

        data = result.data;
        error = result.error;

        if (!error && asRecord(data).success === true) {
            return asRecord(data);
        }

        const errorText = `${error?.message || ''} ${String(asRecord(data).error || '')}`;
        const retryable = isRetryableTransientError(errorText);
        const hasAttemptsLeft = attempt < maxAttempts;
        if (!retryable || !hasAttemptsLeft) {
            break;
        }

        await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
    }

    throw new Error(
        error?.message ||
            (typeof asRecord(data).error === 'string'
                ? String(asRecord(data).error)
                : input.fallbackMessage)
    );
}

export async function createConsentInstanceFromCase(input: {
    caseId: string;
    templateId: string;
    doctorId: string;
    appointmentId?: string;
    treatmentId?: string;
    actor: string;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('create_consent_instance_from_case', {
        p_case_id: input.caseId,
        p_template_id: input.templateId,
        p_doctor_id: input.doctorId,
        p_appointment_id: input.appointmentId || null,
        p_treatment_id: input.treatmentId || null,
        p_actor: input.actor,
    });

    if (error || !data) {
        throw new Error(error?.message || 'Consent-Instanz konnte nicht erstellt werden.');
    }

    return asRecord(data);
}

export async function advanceConsentStep(input: {
    consentId: string;
    step:
        | 'patient_education'
        | 'procedure_explanation'
        | 'risks_alternatives'
        | 'patient_questions'
        | 'doctor_notes'
        | 'ready_for_signatures'
        | 'revision_safe_pdf'
        | 'emergency_override';
    actor: string;
    payload?: Record<string, unknown>;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const sanitizedPayload = sanitizeRpcPayload(input.payload);
    const { data, error } = await sb.rpc('advance_consent_step', {
        p_consent_id: input.consentId,
        p_step: input.step,
        p_actor: input.actor,
        p_payload: sanitizedPayload,
    });

    if (error || !data) {
        throw new Error(error?.message || 'Consent-Schritt konnte nicht fortgeführt werden.');
    }

    return asRecord(data);
}

export async function submitConsentSignature(input: {
    consentId: string;
    signerRole: 'patient' | 'doctor';
    signatureType: string;
    signatureValue: string;
    actor: string;
    signatureRequestId?: string;
    signatureAuditId?: string;
    artifactId?: string;
    clientIp?: string;
    clientUserAgent?: string;
    signerConfirmedIdentity?: boolean;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('submit_consent_signature', {
        p_consent_id: input.consentId,
        p_signer_role: input.signerRole,
        p_signature_type: input.signatureType,
        p_signature_value: input.signatureValue,
        p_actor: input.actor,
        p_signature_request_id: input.signatureRequestId || null,
        p_signature_audit_id: input.signatureAuditId || null,
        p_artifact_id: input.artifactId || null,
        p_client_ip: input.clientIp || null,
        p_client_user_agent: input.clientUserAgent || null,
        p_signer_confirmed_identity: input.signerConfirmedIdentity ?? false,
    });

    if (error || !data) {
        throw new Error(error?.message || 'Consent-Signatur konnte nicht gespeichert werden.');
    }

    return asRecord(data);
}

export async function generateConsentPdf(input: {
    consentId: string;
    actor?: string;
}): Promise<RpcResult> {
    return invokeConsentArtifactGeneration({
        consentId: input.consentId,
        actor: input.actor,
        fallbackMessage: 'Consent-PDF konnte nicht erzeugt werden.',
    });
}

/**
 * Calls the Edge Function with format=csv to generate a CSV export of the consent.
 */
export async function generateConsentCsv(input: {
    consentId: string;
    actor?: string;
}): Promise<RpcResult> {
    return invokeConsentArtifactGeneration({
        consentId: input.consentId,
        actor: input.actor,
        format: 'csv',
        fallbackMessage: 'CSV-Export konnte nicht erstellt werden.',
    });
}

export async function submitConsentSignatureAndFinalizePdf(input: {
    consentId: string;
    signerRole: 'patient' | 'doctor';
    signatureType: string;
    signatureValue: string;
    actor: string;
    signatureRequestId?: string;
    signatureAuditId?: string;
    artifactId?: string;
    clientIp?: string;
    clientUserAgent?: string;
    signerConfirmedIdentity?: boolean;
}): Promise<RpcResult> {
    const signatureResult = await submitConsentSignature(input);
    const signed = Boolean(signatureResult.signed);

    if (!signed) {
        return {
            ...signatureResult,
            pdf_artifact: null,
        };
    }

    const existingRef = await getGeneratedConsentPdfReference(input.consentId);
    if (existingRef) {
        const existingArtifact = asFinalizedPdfArtifact(input.consentId, existingRef);
        if (existingArtifact) {
            return {
                ...signatureResult,
                pdf_artifact: existingArtifact,
            };
        }
    }

    const pdfArtifact = await generateConsentPdf({
        consentId: input.consentId,
        actor: input.actor,
    });

    const refreshedRef = await getGeneratedConsentPdfReference(input.consentId);
    const refreshedArtifact = refreshedRef
        ? asFinalizedPdfArtifact(input.consentId, refreshedRef)
        : null;

    if (!refreshedArtifact) {
        await advanceConsentStep({
            consentId: input.consentId,
            step: 'revision_safe_pdf',
            actor: input.actor,
            payload: {
                revision: Number(pdfArtifact.revision || 0),
                artifact_hash_sha256:
                    typeof pdfArtifact.artifact_hash_sha256 === 'string'
                        ? pdfArtifact.artifact_hash_sha256
                        : null,
                artifact_bucket:
                    typeof pdfArtifact.artifact_bucket === 'string'
                        ? pdfArtifact.artifact_bucket
                        : null,
                artifact_path:
                    typeof pdfArtifact.artifact_path === 'string'
                        ? pdfArtifact.artifact_path
                        : null,
                generated_at:
                    typeof pdfArtifact.generated_at === 'string' ? pdfArtifact.generated_at : null,
            },
        });
    }

    return {
        ...signatureResult,
        pdf_artifact: refreshedArtifact || pdfArtifact,
    };
}

export async function revokeConsentInstance(input: {
    consentId: string;
    reason: string;
    actor: string;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('revoke_consent_instance', {
        p_consent_id: input.consentId,
        p_reason: input.reason,
        p_actor: input.actor,
    });

    if (error || !data) {
        throw new Error(error?.message || 'Consent konnte nicht widerrufen werden.');
    }

    return asRecord(data);
}

export async function refuseConsentInstance(input: {
    consentId: string;
    reason: string;
    actor: string;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('refuse_consent_instance', {
        p_consent_id: input.consentId,
        p_reason: input.reason,
        p_actor: input.actor,
        p_client_ip: null,
        p_client_user_agent: navigator.userAgent,
    });

    if (error || !data) {
        throw new Error(error?.message || 'Verweigerung konnte nicht dokumentiert werden.');
    }

    return asRecord(data);
}

export async function getConsentEvidenceSummary(consentId: string): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('get_consent_evidence_summary', {
        p_consent_id: consentId,
    });

    if (error || !data) {
        throw new Error(error?.message || 'Consent-Evidenz konnte nicht geladen werden.');
    }

    return asRecord(data);
}

export async function getConsentAnalyticsSummary(input: {
    practiceId: string;
    from?: string;
    to?: string;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('get_consent_analytics_summary', {
        p_practice_id: input.practiceId,
        p_from: input.from || null,
        p_to: input.to || null,
    });

    if (error || !data) {
        throw new Error(error?.message || 'Consent-Analytics konnten nicht geladen werden.');
    }

    return asRecord(data);
}

export async function isCaseProcedureUnlocked(caseId: string): Promise<boolean> {
    if (!isSupabaseConfigured()) return false;

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('is_case_procedure_unlocked', {
        p_case_id: caseId,
    });

    if (error) {
        throw new Error(error.message || 'Procedure-Unlock Status konnte nicht ermittelt werden.');
    }

    return Boolean(data);
}

export async function isCaseConsentRequired(caseId: string): Promise<boolean> {
    if (!isSupabaseConfigured()) return false;

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('case_requires_surgical_consent', {
        p_case_id: caseId,
    });

    if (error) {
        throw new Error(error.message || 'Consent-Pflicht konnte nicht ermittelt werden.');
    }

    return Boolean(data);
}

export async function getGeneratedConsentPdfReference(consentId: string): Promise<RpcResult | null> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb
        .from('consent_instances')
        .select('id, generated_document')
        .eq('id', consentId)
        .maybeSingle();

    if (error || !data || !data.generated_document || typeof data.generated_document !== 'object') {
        return null;
    }

    const doc = data.generated_document as RpcResult;
    return {
        consent_id: consentId,
        revision: Number(doc.revision || 0),
        artifact_id: typeof doc.artifact_id === 'string' ? doc.artifact_id : null,
        artifact_hash_sha256:
            typeof doc.artifact_hash_sha256 === 'string' ? doc.artifact_hash_sha256 : null,
        artifact_url: typeof doc.artifact_url === 'string' ? doc.artifact_url : null,
        artifact_bucket: typeof doc.artifact_bucket === 'string' ? doc.artifact_bucket : null,
        artifact_path: typeof doc.artifact_path === 'string' ? doc.artifact_path : null,
        pdf_label: typeof doc.pdf_label === 'string' ? doc.pdf_label : null,
    };
}

/**
 * Loads a consent PDF through an authenticated Edge Function proxy and returns a browser-local blob URL.
 * This avoids exposing Supabase Storage signed URLs directly to the browser or copied links.
 */
export async function downloadConsentDocument(consentId: string): Promise<string | null> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const accessToken = await ensureValidSession();
    if (!accessToken) {
        throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');
    }

    return downloadConsentDocumentUrl(consentId, accessToken);
}

// ─── Read-Only Queries (Web-Parity) ──────────────────────────────────────────

export async function getConsentInstance(consentId: string): Promise<RpcResult | null> {
    if (!isSupabaseConfigured()) return null;
    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung.');
    const sb = requireSupabase();
    const { data, error } = await sb
        .from('consent_instances')
        .select('*')
        .eq('id', consentId)
        .maybeSingle();
    if (error || !data) return null;
    return asRecord(data);
}

export async function listConsentInstancesByCase(caseId: string): Promise<RpcResult[]> {
    if (!isSupabaseConfigured()) return [];
    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung.');
    const sb = requireSupabase();
    const { data, error } = await sb
        .from('consent_instances')
        .select('*')
        .eq('case_id', caseId)
        .order('created_at', { ascending: false });
    if (error || !Array.isArray(data)) return [];
    return data.map(asRecord);
}

export async function listConsentRiskAcknowledgements(consentId: string): Promise<RpcResult[]> {
    if (!isSupabaseConfigured()) return [];
    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung.');
    const sb = requireSupabase();
    const { data, error } = await sb
        .from('consent_risk_acknowledgements')
        .select('id, consent_instance_id, risk_id, acknowledged_at, acknowledged_by')
        .eq('consent_instance_id', consentId)
        .order('acknowledged_at', { ascending: true });
    if (error || !Array.isArray(data)) return [];
    return data.map(asRecord);
}

export async function listConsentModuleInstances(consentId: string): Promise<RpcResult[]> {
    if (!isSupabaseConfigured()) return [];
    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung.');
    const sb = requireSupabase();
    const { data, error } = await sb
        .from('consent_module_instances')
        .select(
            'id, consent_instance_id, module_id, status, completed_at, module_data, consent_modules!inner(module_type, module_name)'
        )
        .eq('consent_instance_id', consentId)
        .order('created_at', { ascending: true });
    if (error || !Array.isArray(data)) return [];
    return data.map((raw) => {
        const row = asRecord(raw);
        const module = asRecord(row.consent_modules);
        return {
            ...row,
            module_type: module.module_type,
            module_name: module.module_name,
        };
    });
}

export async function upsertConsentModuleInstance(input: {
    consentId: string;
    moduleId: string;
    status: 'pending' | 'in_progress' | 'completed' | 'waived';
    moduleData?: Record<string, unknown>;
}): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');
    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung.');
    const sb = requireSupabase();

    const { data: instance, error: instanceError } = await sb
        .from('consent_instances')
        .select('practice_id')
        .eq('id', input.consentId)
        .maybeSingle();

    if (instanceError || !instance?.practice_id) {
        throw new Error('Consent-Modulkontext konnte nicht aufgelöst werden.');
    }

    const { error } = await sb.from('consent_module_instances').upsert(
        {
            consent_instance_id: input.consentId,
            module_id: input.moduleId,
            practice_id: instance.practice_id,
            status: input.status,
            completed_at: input.status === 'completed' ? new Date().toISOString() : null,
            module_data: input.moduleData || {},
        },
        { onConflict: 'consent_instance_id,module_id' },
    );

    if (error) {
        throw new Error(error.message || 'Consent-Modulstatus konnte nicht gespeichert werden.');
    }
}

export async function getConsentTemplateDetail(templateId: string): Promise<RpcResult | null> {
    if (!isSupabaseConfigured()) return null;
    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung.');
    const sb = requireSupabase();
    const [templateRes, sectionsRes, risksRes, questionsRes] = await Promise.all([
        sb.from('consent_templates').select('*').eq('id', templateId).maybeSingle(),
        sb.from('consent_sections').select('*').eq('template_id', templateId).order('display_order'),
        sb.from('consent_risks').select('*').eq('template_id', templateId).order('display_order'),
        sb.from('consent_questions').select('*').eq('template_id', templateId).order('display_order'),
    ]);
    if (templateRes.error || !templateRes.data) return null;
    return {
        template: asRecord(templateRes.data),
        sections: Array.isArray(sectionsRes.data) ? sectionsRes.data : [],
        risks: Array.isArray(risksRes.data) ? risksRes.data : [],
        questions: Array.isArray(questionsRes.data) ? questionsRes.data : [],
    };
}

/**
 * List all consent instances for a given practice, ordered by most recent first.
 * Joins patient + template basic info for the list view.
 */
export async function listConsentInstancesByPractice(practiceId: string): Promise<RpcResult[]> {
    if (!isSupabaseConfigured()) return [];
    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung.');
    const sb = requireSupabase();
    const { data, error } = await sb
        .from('consent_instances')
        .select(
            'id, status, procedure_name, procedure_code, created_at, signed_at, updated_at, ' +
            'patient:patients!consent_instances_patient_id_fkey(id, first_name, last_name, date_of_birth), ' +
            'doctor:doctors!consent_instances_doctor_id_fkey(id, title, first_name, last_name), ' +
            'template:consent_templates!consent_instances_template_id_fkey(id, name)'
        )
        .eq('practice_id', practiceId)
        .order('created_at', { ascending: false })
        .limit(200);
    if (error || !Array.isArray(data)) return [];
    return data.map(asRecord);
}

/**
 * Atomic RPC: resolves or auto-provisions a consent instance for a patient.
 * Creates case + consent if none exist. Idempotent — safe to call repeatedly.
 *
 * Returns: { id, case_id, status, resolved_via }
 */
export async function sendConsentCopyEmail(input: {
    consentId: string;
    recipientEmail?: string;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.functions.invoke('send-consent-copy-email', {
        body: {
            consentId: input.consentId,
            recipientEmail: input.recipientEmail || null,
        },
    });

    if (error) {
        throw await toEdgeFunctionError(
            'send-consent-copy-email',
            error,
            'Consent-Kopie konnte nicht per E-Mail versendet werden.'
        );
    }

    return asRecord(data);
}

export async function ensureConsentForPatient(input: {
    patientId: string;
    practiceId: string;
    doctorId: string;
    actor: string;
}): Promise<RpcResult> {
    if (!isSupabaseConfigured()) throw new Error('Supabase ist nicht konfiguriert.');

    const token = await ensureValidSession();
    if (!token) throw new Error('Keine gültige Sitzung. Bitte erneut anmelden.');

    const sb = requireSupabase();
    const { data, error } = await sb.rpc('ensure_consent_for_patient', {
        p_patient_id: input.patientId,
        p_practice_id: input.practiceId,
        p_doctor_id: input.doctorId,
        p_actor: input.actor,
    });

    if (error || !data) {
        throw new Error(
            error?.message || 'Consent-Instanz konnte nicht aufgelöst oder erstellt werden.'
        );
    }

    return asRecord(data);
}
